import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql, useStaticQuery, withPrefix } from 'gatsby';
import { Heading } from 'lucid-components-react';
import React, { useContext, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';

import AbTestBranch from 'components/ab-test-branch/ab-test-branch';
import AbTest, { PossibleTrigger, Variation } from 'components/ab-test/ab-test';
import NineteenAction from 'components/ab-tests/nineteen-action-custom-google/nineteen-action';
import { RemoteWorkImage } from 'components/ab-tests/remote-work-message-home-page/remote-work-message-home-page';
import LayoutContext from 'contexts/layoutContext';
import { Drupal_ParagraphNineteenHeroImage } from 'generated/graphql';
import './nineteen-hero-image-swap.scss';

interface Props {
  entity: Drupal_ParagraphNineteenHeroImage;
}

const NineteenHeroImage = ({
  entity: {
    fieldHeader,
    fieldHeaderPiece,
    fieldSubheader,
    fieldImage,
    fieldVideoLink,
    fieldAutoplay,
    fieldVideoInitialLoop,
    fieldNineteenAction
  }
}: Props) => {
  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_abtest_hph1_heading_4
              }
            }
          }
        }
      }
    `
  );

  const {
    video: { getVideoModal },
    language: { i18nKey, langPrefix },
    location: { pathname }
  } = useContext(LayoutContext);

  const i18n = i18nFunct(i18nData, i18nKey);

  const [isEnded, setIsEnded] = useState(!fieldVideoInitialLoop);

  const getImageHtml = () =>
    fieldVideoInitialLoop ? (
      <video
        autoPlay
        muted
        playsInline
        className="nineteen-hero-image-swap__image-tag"
        onEnded={() => {
          setIsEnded(true);
        }}
      >
        <source src={fieldVideoInitialLoop} />
      </video>
    ) : (
      <img
          alt={fieldHeader!}
          className="nineteen-hero-image-swap__image-tag"
          src={fieldImage!}
      />
      );

  const hasHeaderPieces = fieldHeaderPiece && fieldHeaderPiece.length > 0;

  return (
    <AbTest
      aboveTheFold
      conditionalTrigger={
        langPrefix === 'en' &&
        (pathname === withPrefix('/') || pathname === withPrefix('/20vc'))
      }
      test="Y20Q2_RemoteWorkMessageHomePage"
      trigger={PossibleTrigger.Load}
    >
      <AbTestBranch branch={Variation.A}>
        <header className="nineteen-hero-image-swap nineteen-hero-image-swap--remove-gradient">
          <div className="nineteen-hero-image-swap__wrapper lucid-container lucid-container--flex">
            <div
              className={`nineteen-hero-image-swap__content ${
                langPrefix !== 'en'
                  ? 'nineteen-hero-image-swap__content--full-width'
                  : ''
                }`}
            >
              <Heading variant="headlineM">
                {hasHeaderPieces
                  ? ReactHtmlParser(fieldHeaderPiece!.join('<br/>'))
                  : fieldHeader}
              </Heading>
              <p className="nineteen-hero-image-swap__subheader">
                {`${fieldSubheader} ${i18n('mktg-abtest-hph1-heading-4')}`}
              </p>

              {fieldNineteenAction && fieldNineteenAction.entity && (
                <NineteenAction entity={fieldNineteenAction.entity} />
              )}
            </div>
            {fieldVideoLink ? (
              <button
                className={`nineteen-hero-image-swap__image-button ${
                  isEnded ? 'nineteen-hero-image-swap__image-button--ended' : ''
                  }`}
                onClick={() =>
                  getVideoModal(fieldVideoLink, fieldAutoplay || undefined)}
                type="button"
              >
                {getImageHtml()}
              </button>
            ) : (
              <div className="nineteen-hero-image-swap__image">
                {getImageHtml()}
              </div>
              )}
          </div>
        </header>
      </AbTestBranch>
      <AbTestBranch branch={Variation.B}>
        <header className="nineteen-hero-image-swap nineteen-hero-image-swap--remove-gradient">
          <div className="nineteen-hero-image-swap__wrapper lucid-container lucid-container--flex">
            <div
              className={`nineteen-hero-image-swap__content ${
                langPrefix !== 'en'
                  ? 'nineteen-hero-image-swap__content--full-width'
                  : ''
                }`}
            >
              <Heading
                as="h1"
                fontSize={langPrefix === 'en' ? '10' : '9'}
                maxWidth={{ default: '100%', mobile: '18rem', tablet: '33rem' }}
                mb={{ default: 3, tablet: 4 }}
                variant="headlineM"
              >
                The visual workspace for remote teams
              </Heading>
              <p className="nineteen-hero-image-swap__subheader nineteen-hero-image-swap__subheader-ab-test-Y20Q2_RemoteWorkMessageHomePage">
                Accomplish more by collaborating in real time. Accelerate
                understanding and drive innovation with powerful diagramming,
                whiteboarding, and data visualization. Sign up for a free trial
                today.
              </p>

              {fieldNineteenAction && fieldNineteenAction.entity && (
                <NineteenAction entity={fieldNineteenAction.entity} />
              )}
            </div>
            {fieldVideoLink ? (
              <button
                className={`nineteen-hero-image-swap__image-button ${
                  isEnded ? 'nineteen-hero-image-swap__image-button--ended' : ''
                  }`}
                onClick={() =>
                  getVideoModal(fieldVideoLink, fieldAutoplay || undefined)}
                type="button"
              >
                {getImageHtml()}
              </button>
            ) : (
              <div className="nineteen-hero-image-swap__image">
                <RemoteWorkImage />
              </div>
              )}
          </div>
        </header>
      </AbTestBranch>
    </AbTest>
  );
};

export default NineteenHeroImage;

export const query = graphql`
  fragment nineteenHeroImage on Drupal_ParagraphNineteenHeroImage {
    fieldHeader
    fieldHeaderPiece
    fieldSubheader
    fieldImage
    fieldVideoInitialLoop
    fieldVideoLink
    fieldAutoplay
    fieldNineteenAction {
      entity {
        ...nineteenAction
      }
    }
  }
`;
